<template>
  <!-- Picks a product replica by name (see `replicas` below) for StoryReplicaStage (hero and sticky
       chapters). Each replica is its own lazy chunk, so a page only loads the one it shows; all are
       server-rendered.
       The `.rp` styles below are the app tokens every replica shares: the shell's page and wash
       colours, onSurface hairlines, Space Grotesk, the black primary button and the status chip
       tones of the invoice list (invoices_table_view_widget.dart). -->
  <component :is="replica.component" v-if="replica" v-bind="replica.props" :state="state" />
  <div v-else class="rp" />
</template>

<script setup>
const props = defineProps({
  name: { type: String, required: true },
  state: { type: String, default: "" },
});

// One line per replica, sorted by name, so PRs that add a replica don't collide.
const replicas = {
  "accounting-app": { component: defineAsyncComponent(() => import("~/components/motion/AccountingAppWindow.vue")) },
  "after-payment": { component: defineAsyncComponent(() => import("~/components/motion/AfterPaymentWindow.vue")) },
  "ai-chat-app": { component: defineAsyncComponent(() => import("~/components/motion/AiChatAppWindow.vue")) },
  "api-tokens-app": { component: defineAsyncComponent(() => import("~/components/motion/ApiTokensWindow.vue")) },
  "brand-looks-app": { component: defineAsyncComponent(() => import("~/components/motion/BrandLooksWindow.vue")) },
  "calendar-app": { component: defineAsyncComponent(() => import("~/components/motion/CalendarAppWindow.vue")) },
  "change-order-app": { component: defineAsyncComponent(() => import("~/components/motion/ChangeOrderAppWindow.vue")) },
  "client-pay": { component: defineAsyncComponent(() => import("~/components/motion/ClientPayWindow.vue")) },
  "client-portal": { component: defineAsyncComponent(() => import("~/components/motion/ClientPortalWindow.vue")) },
  "comms-app": { component: defineAsyncComponent(() => import("~/components/motion/PostCallAppWindow.vue")) },
  "contract-app": { component: defineAsyncComponent(() => import("~/components/motion/ContractAppWindow.vue")) },
  "contract-builder": { component: defineAsyncComponent(() => import("~/components/motion/ContractBuilderWindow.vue")) },
  "contract-sign": { component: defineAsyncComponent(() => import("~/components/motion/ContractSignWindow.vue")) },
  "currency-app": { component: defineAsyncComponent(() => import("~/components/motion/CurrencyAppWindow.vue")) },
  "expenses-app": { component: defineAsyncComponent(() => import("~/components/motion/ExpensesAppWindow.vue")) },
  "files-app": { component: defineAsyncComponent(() => import("~/components/motion/FilesAppWindow.vue")) },
  "gmail-app": { component: defineAsyncComponent(() => import("~/components/motion/MailboxAppWindow.vue")), props: { provider: "gmail" } },
  "home-app": { component: defineAsyncComponent(() => import("~/components/motion/HomeAppWindow.vue")) },
  "invoice-editor": { component: defineAsyncComponent(() => import("~/components/motion/InvoiceEditorWindow.vue")) },
  "invoice-list": { component: defineAsyncComponent(() => import("~/components/motion/InvoiceListWindow.vue")) },
  "invoices-app": { component: defineAsyncComponent(() => import("~/components/motion/InvoicesAppWindow.vue")) },
  "ivr-app": { component: defineAsyncComponent(() => import("~/components/motion/IvrAppWindow.vue")) },
  "m365-app": { component: defineAsyncComponent(() => import("~/components/motion/MailboxAppWindow.vue")), props: { provider: "m365" } },
  "mobile-app": { component: defineAsyncComponent(() => import("~/components/motion/MobileAppWindow.vue")) },
  "payment-methods": { component: defineAsyncComponent(() => import("~/components/motion/PaymentMethodsWindow.vue")) },
  "payments-app": { component: defineAsyncComponent(() => import("~/components/motion/PaymentsAppWindow.vue")) },
  "pdf-app": { component: defineAsyncComponent(() => import("~/components/motion/PdfTemplateWindow.vue")) },
  "portal-app": { component: defineAsyncComponent(() => import("~/components/motion/PortalAppWindow.vue")) },
  "portal-settings": { component: defineAsyncComponent(() => import("~/components/motion/PortalSettingsWindow.vue")) },
  "post-call-app": { component: defineAsyncComponent(() => import("~/components/motion/PostCallAppWindow.vue")) },
  "products-app": { component: defineAsyncComponent(() => import("~/components/motion/ProductsAppWindow.vue")) },
  "proposal-app": { component: defineAsyncComponent(() => import("~/components/motion/ProposalAppWindow.vue")) },
  "proposal-client": { component: defineAsyncComponent(() => import("~/components/motion/ProposalClientWindow.vue")) },
  "proposal-editor": { component: defineAsyncComponent(() => import("~/components/motion/ProposalEditorWindow.vue")) },
  "retainer-create": { component: defineAsyncComponent(() => import("~/components/motion/RetainerCreateWindow.vue")) },
  "retainer-detail": { component: defineAsyncComponent(() => import("~/components/motion/RetainerDetailWindow.vue")) },
  "retainers-app": { component: defineAsyncComponent(() => import("~/components/motion/RetainersAppWindow.vue")) },
  "stripe-app": { component: defineAsyncComponent(() => import("~/components/motion/StripeAppWindow.vue")) },
  "support-app": { component: defineAsyncComponent(() => import("~/components/motion/SupportAppWindow.vue")) },
  "themes-app": { component: defineAsyncComponent(() => import("~/components/motion/ThemesAppWindow.vue")) },
  "time-app": { component: defineAsyncComponent(() => import("~/components/motion/TimeTrackingAppWindow.vue")) },
  "today-app": { component: defineAsyncComponent(() => import("~/components/motion/AutopilotTodayWindow.vue")) },
  "voice-app": { component: defineAsyncComponent(() => import("~/components/motion/PostCallAppWindow.vue")) },
};

const replica = computed(() => replicas[props.name] || null);
</script>

<style>
.rp {
  --rp-page: #fff;
  --rp-wash: #f4f6f8;
  --rp-card: #fff;
  --rp-line: rgb(0 0 0 / 0.1);
  --rp-text: #171717;
  --rp-muted: #5f6368;
  --rp-faint: #6b7078;
  --rp-ink: #171717;
  --rp-ink-text: #fff;
  --rp-hover: #eef1f4;
  --rp-link: #3f58bf;
  --rp-green: #2d7a4e;
  --rp-green-fill: #e6f4ea;
  --rp-blue: #2f5bd3;
  --rp-blue-fill: #e8eefc;
  --rp-amber: #9a5b00;
  --rp-amber-fill: #fdf0d5;
  --rp-red: #c0352b;
  --rp-red-fill: #fdecea;
  --rp-grey-fill: #eef0f2;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--rp-line);
  border-radius: 10px;
  background: var(--rp-wash);
  color: var(--rp-text);
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  letter-spacing: 0;
  text-align: left;
  box-shadow: 0 18px 50px -18px rgb(15 23 42 / 0.28), 0 2px 6px rgb(15 23 42 / 0.06);
}
.dark .rp {
  --rp-page: #1c1c1c;
  --rp-wash: #242424;
  --rp-card: #262626;
  --rp-line: rgb(255 255 255 / 0.13);
  --rp-text: #ececec;
  --rp-muted: #a8a8a8;
  --rp-faint: #9a9a9a;
  --rp-ink: #ececec;
  --rp-ink-text: #171717;
  --rp-hover: #2f2f2f;
  --rp-link: #93aef9;
  --rp-green: #6ed39a;
  --rp-green-fill: rgb(63 178 110 / 0.18);
  --rp-blue: #93aef9;
  --rp-blue-fill: rgb(91 127 234 / 0.2);
  --rp-amber: #f1c95e;
  --rp-amber-fill: rgb(241 201 94 / 0.16);
  --rp-red: #ff8a80;
  --rp-red-fill: rgb(255 107 97 / 0.16);
  --rp-grey-fill: #333;
  box-shadow: 0 18px 50px -18px rgb(0 0 0 / 0.7);
}
.rp svg { width: 14px; height: 14px; flex: none; fill: currentColor; }
.rp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid var(--rp-line);
  background: var(--rp-page);
  padding: 10px 14px;
}
.rp-head b { font-size: 14px; font-weight: 600; }
.rp-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 6px;
  background: var(--rp-ink);
  padding: 5px 10px;
  color: var(--rp-ink-text);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.rp-btn-quiet {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--rp-line);
  border-radius: 6px;
  background: var(--rp-card);
  padding: 4px 9px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.rp-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 5px;
  background: var(--rp-grey-fill);
  padding: 2px 7px;
  color: var(--rp-muted);
  font-size: 11.5px;
  font-style: normal;
  font-weight: 600;
  white-space: nowrap;
}
.rp-chip svg { width: 11px; height: 11px; }
.rp-chip--green { background: var(--rp-green-fill); color: var(--rp-green); }
.rp-chip--blue { background: var(--rp-blue-fill); color: var(--rp-blue); }
.rp-chip--amber { background: var(--rp-amber-fill); color: var(--rp-amber); }
.rp-chip--red { background: var(--rp-red-fill); color: var(--rp-red); }
.rp-num { font-variant-numeric: tabular-nums; }
/* A toast the replicas slide in at the bottom (notifications, "sent" confirmations). */
.rp-toast {
  position: absolute;
  left: 50%;
  bottom: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: calc(100% - 28px);
  border-radius: 8px;
  background: var(--rp-ink);
  padding: 8px 12px;
  color: var(--rp-ink-text);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 10px 24px -10px rgb(0 0 0 / 0.45);
  transform: translate(-50%, 0);
  transition: opacity 0.3s ease, transform 0.4s cubic-bezier(0.33, 1, 0.68, 1);
}
.rp-toast.is-off { opacity: 0; transform: translate(-50%, 12px); }
@media (prefers-reduced-motion: reduce) {
  .rp *, .rp-toast { transition: none !important; animation: none !important; }
}
</style>
